import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";
import { atomWithStorage } from "jotai/utils";

import {
  type Account,
  type Conversation,
  type EndUserPanel,
  type Message,
} from "shared/apiClient";
import { type Noticeboard } from "shared/lib";

import { ShowReportState } from "@/types/reports";

export const Sections = {
  Address: "Address",
  InvalidAddress: "InvalidAddress",
  AddressError: "AddressError",
  Loading: "Loading",
} as const;
export type Sections = (typeof Sections)[keyof typeof Sections];

export const InvalidAddressType = {
  ADDRESS_OUT_OF_SCOPE: "ADDRESS_OUT_OF_SCOPE",
  COUNCIL_NOT_SUPPORTED: "COUNCIL_NOT_SUPPORTED",
  DOWN_FOR_MAINTENANCE: "DOWN_FOR_MAINTENANCE",
  TECHNICAL_ERROR: "TECHNICAL_ERROR",
  TRANSLATION_EXPERT_NOT_FOUND: "TRANSLATION_EXPERT_NOT_FOUND",
} as const;
export type InvalidAddressType =
  (typeof InvalidAddressType)[keyof typeof InvalidAddressType];

export const wizardNavigatorVar = atom<Sections>(Sections.Address);
export const invalidAddressTypeVar = atom<InvalidAddressType>();

export const chatLockVar = atom<boolean>(false);

export enum Tabs {
  Chat = "chat",
  Lot = "lot",
  Proposal = "proposal",
  Permissibility = "permissibility",
  LotAndProposal = "lotAndProposal",
}

export const tabSelectedVar = atom<Tabs>(Tabs.Permissibility);

export const tabAnimationVar = atom<boolean>(false);

export type TabNotifications = {
  lot: boolean;
  proposal: boolean;
  permissibility: boolean;
  chat: boolean;
};

export const tabNotificationsVar = atom<TabNotifications>({
  lot: false,
  proposal: false,
  permissibility: false,
  chat: false,
});

export enum Pages {
  Auth = "Auth",
  Address = "Address",
  App = "App",
}

export const applicationPageVar = atom<Pages>(Pages.Address);

export const isLoadingVar = atom<boolean>(false);

export enum AuthFlow {
  ForgotPassword = "ForgotPassword",
  Login = "Login",
  Signup = "Signup",
}

export const authFlowSelectedVar = atom<AuthFlow>(AuthFlow.Signup);

export const isAuthenticatedVar = atom<boolean>(false);

export const showReportVar = atomWithStorage<ShowReportState>(
  "showReport",
  {
    visible: false,
    conversationId: "",
  },
  undefined,
  { getOnInit: true },
);

export const expertPanelVar = atomWithStorage<EndUserPanel | null>(
  "enquiry-panel",
  null,
  undefined,
  { getOnInit: true },
);

//-----------------------------------------------------------------------------

export const skipAnalysisLayoutAnimationsVar = atom<boolean>(false);

//-----------------------------------------------------------------------------

export const accountVar = atomWithImmer<Account | null>(null);
export const conversationOverrideVar = atomWithImmer<Conversation | null>(null);
export const messagesVar = atom<Conversation["messages"]>([]);
export const noticeboardDataVar = atomWithImmer<Noticeboard>({});

//-----------------------------------------------------------------------------
export const debugMessageOffsetVar = atom<number>(0);
export const debugMessageVar = atom<Message | null>(null);

export const languageVar = atomWithStorage<string>("lang", "en", undefined, {
  getOnInit: true,
});

export const multiLingualToastOpenVar = atomWithStorage(
  "multiLingualToastOpen",
  false,
  undefined,
  {
    getOnInit: true,
  },
);
